import { z } from 'zod';

/**
 * SIL-1830 - one scheme gate for every URL that is stored or API-supplied and
 * later rendered into an `href`. React escapes text but not an `href` scheme,
 * so a stored `javascript:` URL runs on click. Allow http(s) only.
 *
 * Parsing (not a prefix regex) is deliberate: the WHATWG URL parser strips
 * tab/CR/LF anywhere in the input and lowercases the scheme, so `JaVa\tScRiPt:`
 * parses as `javascript:` and is rejected here, exactly as a browser would
 * resolve it.
 */

/** The browser-resolved href when `raw` is an http(s) URL, else null. */
export function safeHttpUrl(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let u: URL;
  try {
    u = new URL(trimmed);
  } catch {
    return null;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
  return u.href;
}

/**
 * Validator for URLs we STORE (and later link): http(s) only, no embedded
 * credentials (`https://user:pw@host/`). Use instead of bare `z.string().url()`.
 */
export const storedHttpUrlSchema = z
  .string()
  .url()
  .superRefine((val, ctx) => {
    let u: URL;
    try {
      u = new URL(val);
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid URL' });
      return;
    }
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'URL must use http or https' });
    } else if (u.username || u.password) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'URL must not contain credentials' });
    }
  });
